import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetPayRunMemberJobsQuery } from '../api';
import { PayRunMemberJob } from '../confirmJob.model';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { JobReviewStatus } from '../../constants';
import { ColumnInfo } from '../../pay-period-review/payperiod.model';

export interface PayRunJobQueryParams {
  jobStatus?: string | null;
  createdBy?: string;
  customerIds?: string;
  assignees?: string;
  leadSource?: string;
  search?: string | null;
  amount?: string | null;
  scheduledFrom?: string | null;
  scheduledTo?: string | null;
  sortBy?: string;
  sortOrder?: string;
  externalCompany?: string;
  jobReviewStatus?: JobReviewStatus;
  tags?: string[];
}

const initialQueryParamsState: PayRunJobQueryParams = {
  jobStatus: null,
  createdBy: null,
  customerIds: null,
  assignees: null,
  leadSource: null,
  search: null,
  amount: null,
  scheduledFrom: null,
  scheduledTo: null,
  sortBy: 'scheduledDate',
  sortOrder: 'DESC',
  externalCompany: null,
  tags: [],
  jobReviewStatus: null
};

export const usePaginatedPayRunMemberJobsQuery = (payRunId: string, merchantId: string, limit = 50) => {
  const [list, setList] = useState<PayRunMemberJob[]>([]);
  const [queryParams, setQueryParams] = useState<PayRunJobQueryParams>(initialQueryParamsState);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [columnConfig, setColumnConfig] = useState<ColumnInfo[]>([]);
  const page = useRef(1);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const [getPayRunMemberJobs, jobsState] = useLazyGetPayRunMemberJobsQuery();

  useEffect(() => {
    if (!jobsState?.isFetching) {
      if (jobsState?.data) {
        const { jobDetails, totalCount: total, columnInfo } = jobsState.data;
        setList(jobDetails || []);
        setTotalCount(total);
        if (columnInfo && Array.isArray(columnInfo)) {
          const sortedColumnInfo = [...columnInfo].sort((a, b) => a.priority - b.priority);
          setColumnConfig(sortedColumnInfo);
        } else {
          setColumnConfig(columnInfo);
        }
      }
      setIsLoading(false);
    }
  }, [jobsState?.isFetching]);

  useEffect(() => {
    if (!jobsState?.isFetching && jobsState?.isError) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [jobsState?.isError]);

  const fetchData = useCallback(
    (currentPage: number) => {
      setIsLoading(true);
      setIsError(false);
      page.current = currentPage;

      if (currentTeam?.id && payRunId && merchantId) {
        try {
          getPayRunMemberJobs({
            teamId: currentTeam.id,
            payRunId,
            merchantId,
            limit,
            page: page.current - 1,
            ...queryParams,
            // Handle the jobReviewStatus specially since it needs to be an array in the API
            jobReviewStatus: queryParams.jobReviewStatus ? [queryParams.jobReviewStatus] : []
          }).unwrap();
        } catch (err) {
          setIsError(true);
        }
      }
    },
    [currentTeam?.id, payRunId, merchantId, queryParams]
  );

  useEffect(() => {
    fetchData(1);
  }, [queryParams]);

  return {
    isLoading,
    isFetching: jobsState?.isFetching,
    list,
    isError,
    totalCount,
    fetchData,
    setQueryParams,
    queryParams,
    page: page.current,
    columnConfig
  };
};
